"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _clone = _interopRequireDefault(require("clone"));

var _mongoObject = _interopRequireDefault(require("mongo-object"));

var _utility = require("./utility");

var _SimpleSchema = require("./SimpleSchema");

var _convertToProperType = _interopRequireDefault(require("./clean/convertToProperType"));

var _setAutoValues = _interopRequireDefault(require("./clean/setAutoValues"));

var _typeValidator = _interopRequireDefault(require("./validation/typeValidator"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * @param {SimpleSchema} ss - A SimpleSchema instance
 * @param {Object} doc - Document or modifier to clean. Referenced object will be modified in place.
 * @param {Object} [options]
 * @param {Boolean} [options.mutate=false] - Mutate doc. Set this to true to improve performance if you don't mind mutating the object you're cleaning.
 * @param {Boolean} [options.filter=true] - Do filtering?
 * @param {Boolean} [options.autoConvert=true] - Do automatic type converting?
 * @param {Boolean} [options.removeEmptyStrings=true] - Remove keys in normal object or $set where the value is an empty string?
 * @param {Boolean} [options.removeNullsFromArrays=false] - Remove all null items from all arrays
 * @param {Boolean} [options.trimStrings=true] - Trim string values?
 * @param {Boolean} [options.getAutoValues=true] - Inject automatic and default values?
 * @param {Boolean} [options.isModifier=false] - Is doc a modifier object?
 * @param {Boolean} [options.isUpsert=false] - Will the modifier object be used to do an upsert? This is used
 *   to determine whether $setOnInsert should be added to it for defaultValues.
 * @param {Boolean} [options.mongoObject] - If you already have the mongoObject instance, pass it to improve performance
 * @param {Object} [options.extendAutoValueContext] - This object will be added to the `this` context of autoValue functions.
 * @returns {Object} The modified doc.
 *
 * Cleans a document or modifier object. By default, will filter, automatically
 * type convert where possible, and inject automatic/default values. Use the options
 * to skip one or more of these.
 */
function clean(ss, doc) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  // By default, doc will be filtered and autoconverted
  options = _objectSpread({
    isModifier: (0, _utility.looksLikeModifier)(doc),
    isUpsert: false
  }, ss._cleanOptions, {}, options); // Clone so we do not mutate

  var cleanDoc = options.mutate ? doc : (0, _clone.default)(doc);
  var mongoObject = options.mongoObject || new _mongoObject.default(cleanDoc, ss.blackboxKeys()); // Clean loop

  if (options.filter || options.autoConvert || options.removeEmptyStrings || options.trimStrings) {
    var removedPositions = []; // For removing now-empty objects after

    mongoObject.forEachNode(function eachNode() {
      // The value of a $unset is irrelevant, so no point in cleaning it.
      // Also we do not care if fields not in the schema are unset.
      if (this.operator === '$unset') return;
      var gKey = this.genericKey;
      if (!gKey) return;
      var val = this.value;
      if (val === undefined) return;
      var p; // Filter out props if necessary

      if (options.filter && !ss.allowsKey(gKey) || options.removeNullsFromArrays && this.isArrayItem && val === null) {
        // XXX Special handling for $each; maybe this could be made nicer
        if (this.position.slice(-7) === '[$each]') {
          mongoObject.removeValueForPosition(this.position.slice(0, -7));
          removedPositions.push(this.position.slice(0, -7));
        } else {
          this.remove();
          removedPositions.push(this.position);
        }

        if (_SimpleSchema.SimpleSchema.debug) {
          console.info("SimpleSchema.clean: filtered out value that would have affected key \"".concat(gKey, "\", which is not allowed by the schema"));
        }

        return; // no reason to do more
      }

      var outerDef = ss.schema(gKey);
      var defs = outerDef && outerDef.type.definitions;
      var def = defs && defs[0]; // Autoconvert values if requested and if possible

      if (options.autoConvert && def) {
        var isValidType = defs.some(function (definition) {
          var errors = _typeValidator.default.call({
            valueShouldBeChecked: true,
            definition: definition,
            value: val
          });

          return errors === undefined;
        });

        if (!isValidType) {
          var newVal = (0, _convertToProperType.default)(val, def.type);

          if (newVal !== undefined && newVal !== val) {
            _SimpleSchema.SimpleSchema.debug && console.info("SimpleSchema.clean: autoconverted value ".concat(val, " from ").concat(_typeof(val), " to ").concat(_typeof(newVal), " for ").concat(gKey));
            val = newVal;
            this.updateValue(newVal);
          }
        }
      } // Trim strings if
      // 1. The trimStrings option is `true` AND
      // 2. The field is not in the schema OR is in the schema with `trim` !== `false` AND
      // 3. The value is a string.


      if (options.trimStrings && (!def || def.trim !== false) && typeof val === 'string') {
        val = val.trim();
        this.updateValue(val);
      } // Remove empty strings if
      // 1. The removeEmptyStrings option is `true` AND
      // 2. The value is in a normal object or in the $set part of a modifier
      // 3. The value is an empty string.


      if (options.removeEmptyStrings && (!this.operator || this.operator === '$set') && typeof val === 'string' && !val.length) {
        // For a document, we remove any fields that are being set to an empty string
        this.remove(); // For a modifier, we $unset any fields that are being set to an empty string.
        // But only if we're not already within an entire object that is being set.

        if (this.operator === '$set' && this.position.match(/\[.+?\]/g).length < 2) {
          p = this.position.replace('$set', '$unset');
          mongoObject.setValueForPosition(p, '');
        }
      }
    }, {
      endPointsOnly: false
    }); // Remove any objects that are now empty after filtering

    removedPositions.forEach(function (removedPosition) {
      var lastBrace = removedPosition.lastIndexOf('[');

      if (lastBrace !== -1) {
        var removedPositionParent = removedPosition.slice(0, lastBrace);
        var value = mongoObject.getValueForPosition(removedPositionParent);
        if ((0, _utility.isEmptyObject)(value)) mongoObject.removeValueForPosition(removedPositionParent);
      }
    });
    mongoObject.removeArrayItems();
  } // Set automatic values


  options.getAutoValues && (0, _setAutoValues.default)(ss.autoValueFunctions(), mongoObject, options.isModifier, options.isUpsert, options.extendAutoValueContext); // Ensure we don't have any operators set to an empty object
  // since MongoDB 2.6+ will throw errors.

  if (options.isModifier) {
    Object.keys(cleanDoc || {}).forEach(function (op) {
      var operatorValue = cleanDoc[op];

      if (_typeof(operatorValue) === 'object' && operatorValue !== null && (0, _utility.isEmptyObject)(operatorValue)) {
        delete cleanDoc[op];
      }
    });
  }

  return cleanDoc;
}

var _default = clean;
exports.default = _default;
module.exports = exports.default;
module.exports.default = exports.default;